import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Upload, GoogleDrive, Upload5, Cursor04, File02 } from 'blaze-ui/icons/20'
import { HighDetailedWebsite, HighDetailedTextDoc } from 'blaze-ui/icons/24'
import { DocHighDetailed } from 'blaze-ui/icons/40'
import { useGoogleDriveIntegration } from 'hooks/useGoogleDriveIntegration'
import type { ScreenType } from './constants'
import Styles from './MainSelectionScreen.module.scss'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import { useOpenIntegrations } from 'hooks/useOpenIntegrations'

interface Props {
  onSelect: (screen: ScreenType) => void
}

const EXAMPLES = [
  {
    label: 'Brochures',
    image:
      'https://res.cloudinary.com/almanac/image/upload/v1762549907/blaze_assets/source_materials/brochure-example_agsj9l.webp',
  },
  {
    label: 'One-pagers',
    image:
      'https://res.cloudinary.com/almanac/image/upload/v1762549907/blaze_assets/source_materials/one-pager-example_aoxzlq.webp',
  },
  {
    label: 'Press Coverage',
    image:
      'https://res.cloudinary.com/almanac/image/upload/v1762549907/blaze_assets/source_materials/press-example_vjkmcf.webp',
  },
  {
    label: 'Videos',
    image:
      'https://res.cloudinary.com/almanac/image/upload/v1762549908/blaze_assets/source_materials/video-example_eyrgon.webp',
  },
]

export const MainSelectionScreen = ({ onSelect }: Props) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { isConnected, handleConnect, openPicker } = useGoogleDriveIntegration()
  const openIntegrations = useOpenIntegrations()

  const handleGoogleDriveClick = () => {
    if (isConnected) {
      openPicker()
    } else {
      handleConnect()
    }
  }

  if (isSystemRedesignEnabled) {
    return (
      <div className={Styles.container}>
        <div className={Styles.uploadSection}>
          <div className={Styles.uploadHeader}>
            <div className={Styles.uploadContent}>
              <Upload5 size={28} />
              <Heading level={3}>Upload Files</Heading>
              <Text variant="secondary" color="var(--dark-60)">
                Upload marketing or brand materials (brochures, one-pagers, videos, press coverage, etc.). Do not
                include sensitive or confidential information.
              </Text>
            </div>
            <div className={Styles.uploadActions}>
              <Button frontIcon={Upload} onPress={() => onSelect('file')} size="lg">
                Upload Files
              </Button>
              <div className={Styles.fileFormats}>
                <Text variant="metadata" color="var(--dark-40)">
                  PDF, TXT, Markdown, MOV, MP4
                </Text>
              </div>
            </div>
          </div>
          <div className={Styles.examplesGrid}>
            {EXAMPLES.map(({ label, image }) => (
              <div key={label} className={Styles.exampleCard}>
                <img src={image} alt={label} className={Styles.exampleImage} />
                <Text variant="metadata" color="var(--dark-40)">
                  {label}
                </Text>
              </div>
            ))}
          </div>
        </div>

        <div className={Styles.alternativesGrid}>
          <button className={Styles.alternativeCard} onClick={() => onSelect('website')}>
            <div className={Styles.cardContent}>
              <div className={Styles.alternativeIcon}>
                <Cursor04 size={28} />
              </div>
              <Text>Add Website</Text>
            </div>
          </button>

          <button className={Styles.alternativeCard} onClick={openIntegrations}>
            <div className={Styles.cardContent}>
              <div className={Styles.alternativeIcon}>
                <UserProfileAdd2 size={28} />
              </div>
              <Text>Add Account</Text>
            </div>
          </button>

          <button className={Styles.alternativeCard} onClick={handleGoogleDriveClick}>
            <div className={Styles.cardContent}>
              <div className={Styles.alternativeIcon}>
                <GoogleDrive size={28} />
              </div>
              <Text>{isConnected ? 'Import Files' : 'Connect'}</Text>
            </div>
          </button>

          <button className={Styles.alternativeCard} onClick={() => onSelect('text')}>
            <div className={Styles.cardContent}>
              <div className={Styles.alternativeIcon}>
                <File02 size={28} />
              </div>
              <Text>Paste Text</Text>
            </div>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.uploadSection}>
        <div className={Styles.uploadHeader}>
          <div className={Styles.uploadContent}>
            <div className={Styles.headerRow}>
              <DocHighDetailed size={30} />
              <Heading level={4}>Upload Files</Heading>
            </div>
            <Text variant="secondary" color="var(--dark-80)">
              Upload marketing or brand materials (brochures, one-pagers, videos, press coverage, etc.). Do not include
              sensitive or confidential information.
            </Text>
          </div>
          <div className={Styles.uploadActions}>
            <Button variant="ghost-tertiary" frontIcon={Upload} onPress={() => onSelect('file')} size="lg">
              Upload Files
            </Button>
            <div className={Styles.fileFormats}>
              <Text variant="metadata" color="var(--dark-40)">
                PDF, TXT, Markdown,
                <br />
                MOV, MP4
              </Text>
            </div>
          </div>
        </div>
        <div className={Styles.examplesBlock}>
          <Text variant="label">Examples include:</Text>
          <div className={Styles.examplesGrid}>
            {EXAMPLES.map(({ label, image }) => (
              <div key={label} className={Styles.exampleCard}>
                <img src={image} alt={label} className={Styles.exampleImage} />
                <Text variant="metadata" color="var(--dark-40)">
                  {label}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={Styles.alternativesGrid}>
        <button className={Styles.alternativeCard} onClick={() => onSelect('website')}>
          <div className={Styles.cardContent}>
            <div className={Styles.alternativeIcon}>
              <HighDetailedWebsite size={22} />
            </div>
            <Heading level={4}>Add Website</Heading>
          </div>
        </button>

        <button className={Styles.alternativeCard} onClick={() => onSelect('text')}>
          <div className={Styles.cardContent}>
            <div className={Styles.alternativeIcon}>
              <HighDetailedTextDoc size={26} />
            </div>
            <Heading level={4}>Paste Text</Heading>
          </div>
        </button>

        <button className={Styles.alternativeCard} onClick={handleGoogleDriveClick}>
          <div className={Styles.cardContent}>
            <div className={Styles.alternativeIcon}>
              <GoogleDrive size={24} />
            </div>
            <Heading level={4}>{isConnected ? 'Import Files' : 'Connect'}</Heading>
          </div>
        </button>
      </div>
    </div>
  )
}
